/**
 * "Leave project", for every role: confirm, take yourself out, land on `/`
 * (another project) with a toast. The project's last active owner cannot leave
 * (the server refuses too), so the button says why instead.
 */

import { useState } from "react";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/overlay";
import { errorText } from "@/components/ui/states";
import { useCurrentUser } from "@/features/auth/hooks";
import { activeOwners, useLeaveProject, useMembers, useProject } from "@/features/projects/hooks";
import type { Member } from "@/features/projects/types";
import { useUiStore } from "@/stores/ui-store";

export const ONLY_OWNER = "Only owner — make someone else an owner first.";

/** Is `member` the one active owner left? Then they may not leave or step down. */
export const isOnlyOwner = (member: Member | undefined, members: Member[] | undefined) =>
  !!member && member.role === "owner" && !!member.is_active && activeOwners(members) <= 1;

/** Why the signed-in person may not leave, or null. Only an owner can be the last one. */
function useLeaveBlocked(): string | null {
  const { project, can } = useProject();
  const email = useCurrentUser().data?.email;
  const owner = project?.role === "owner";
  const members = useMembers(owner && can("members:read"));
  const me = members.data?.find((m) => m.email === email);
  return isOnlyOwner(me, members.data) ? ONLY_OWNER : null;
}

export function LeaveProjectDialog({ onClose }: { onClose: () => void }) {
  const { project } = useProject();
  const { leave, pending } = useLeaveProject();
  const notify = useUiStore((s) => s.notify);
  return (
    <Dialog
      title={`Leave "${project?.name ?? "this project"}"?`}
      onClose={onClose}
      width={460}
      onSubmit={() => !pending && leave((e) => notify(errorText(e, "Could not leave the project."), "error"))}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            Leave project
          </Button>
        </>
      }
    >
      <p className="text-[13px] leading-relaxed text-muted">
        You lose access right away. To come back, someone in the project has to add you again.
      </p>
    </Dialog>
  );
}

export function LeaveProjectButton() {
  const blocked = useLeaveBlocked();
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="flex items-center justify-between gap-4 p-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold">Leave project</span>
        <span className="text-[13px] text-muted">
          {blocked ?? "You stop seeing this project. Someone in it can add you again."}
        </span>
      </div>
      <Button disabled={!!blocked} title={blocked ?? undefined} onClick={() => setConfirming(true)}>
        <LogOut size={14} strokeWidth={1.8} aria-hidden />
        Leave project
      </Button>
      {confirming && <LeaveProjectDialog onClose={() => setConfirming(false)} />}
    </div>
  );
}
